// packages/api/src/hooks/useMyBankAccounts.ts
// Source: docs/contracts/ESCR-profile-selfservice.md

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  ApiSuccess,
  CreateProfileBankAccountInput,
  DeleteProfileBankAccountInput,
  ProfileBankAccountMasked,
  UpdateProfileBankAccountInput,
} from '@alamo/types';

import { apiClient } from '../client';
import { queryKeys } from '../query-keys';

export function useMyBankAccounts() {
  return useQuery({
    queryKey: queryKeys.profile.bankAccounts(),
    queryFn: async (): Promise<ProfileBankAccountMasked[]> => {
      const response =
        await apiClient.get<ApiSuccess<ProfileBankAccountMasked[]>>('/profile/bank-accounts');
      return response.data.data;
    },
  });
}

export function useCreateMyBankAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateProfileBankAccountInput): Promise<ProfileBankAccountMasked> => {
      const response = await apiClient.post<ApiSuccess<ProfileBankAccountMasked>>(
        '/profile/bank-accounts',
        input,
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.profile.bankAccounts() });
    },
  });
}

export function useUpdateMyBankAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      input,
    }: {
      id: string;
      input: UpdateProfileBankAccountInput;
    }): Promise<ProfileBankAccountMasked> => {
      const response = await apiClient.patch<ApiSuccess<ProfileBankAccountMasked>>(
        `/profile/bank-accounts/${id}`,
        input,
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.profile.bankAccounts() });
    },
  });
}

export function useDeleteMyBankAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      input,
    }: {
      id: string;
      input: DeleteProfileBankAccountInput;
    }): Promise<void> => {
      await apiClient.delete(`/profile/bank-accounts/${id}`, { data: input });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.profile.bankAccounts() });
    },
  });
}
